import { FC, useEffect, useState, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useLocation } from "react-router-dom";
import AppRoutes from "../../constants/routes/AppRoutes";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { useContent } from "@/hooks/useContent";
import Logo from "@/components/images/Logo";
import useUserTokenStore from "@/stores/user-token.store";
import { requireAccessToken } from "@/lib/auth/accessToken";
import {
  Claims,
  claimsNamespace,
} from "@/components/navigation/WithClaimCheck";
import { jwtDecode } from "jwt-decode";
import AuthThemeToggle from "@/components/theme/AuthThemeToggle";
import LoadingView from "@/components/state-handlers/LoadingView";
import { handleLogout } from "@/utils/auth";
import useUsers from "@/hooks/useUsers";
import useUserStore from "@/stores/user.store";
import { useItemService } from "@/hooks/useServices";
import { User } from "@/models/users/User";
import { useUnleashContext } from "@unleash/proxy-client-react";
import { finalizeLogin } from "@/lib/finalizeLogin";
import { saveProfileOnce } from "@/lib/profileSave";
import { takePendingReturnTo } from "@/lib/auth/authRedirectNavigate";
import { logger } from "@/lib/logger";

const SignInView: FC = () => {
  const {
    loginWithRedirect,
    logout,
    error,
    isAuthenticated,
    isLoading: isAuth0Loading,
    getAccessTokenSilently,
  } = useAuth0();
  const { onLogin, useQueryMe, update } = useUsers();
  const userService = useItemService((services) => services.user);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useContent();
  const updateContext = useUnleashContext();

  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLoggedInOnce, setIsLoggedInOnce] = useState(false);
  const me = useQueryMe(true);
  const hasAttemptedAutoCreate = useRef(false);

  // Attempt to create/update a user automatically for SSO first-login flows.
  const attemptAutoCreateUser = async (
    decodedToken: Claims,
    tenantId: string,
    meUser: User | undefined,
    returnTo?: string
  ) => {
    const givenName = decodedToken[`${claimsNamespace}/given_name`] as
      | string
      | undefined;
    const familyName = decodedToken[`${claimsNamespace}/family_name`] as
      | string
      | undefined;
    const name = decodedToken[`${claimsNamespace}/name`] as string | undefined;
    const email = decodedToken[`${claimsNamespace}/email`] as
      | string
      | undefined;
    const picture = decodedToken[`${claimsNamespace}/picture`] as
      | string
      | undefined;
    const userId = decodedToken[`${claimsNamespace}/user_id`] as
      | string
      | undefined;

    const nameParts = name ? name.split(" ").filter(Boolean) : [];
    const hasFullName = (givenName && familyName) || nameParts.length > 1;
    if (!hasFullName || !userId) return false;

    try {
      // Single-flighted: this view remounts during login, which resets
      // hasAttemptedAutoCreate and used to send the same PUT twice (LAR-436).
      await saveProfileOnce(
        {
          id: userId,
          firstName: givenName || nameParts.slice(0, -1).join(" "),
          lastName: familyName || nameParts[nameParts.length - 1],
          email: email || "",
          profilePictureUrl: picture || "",
        },
        (input) => update.mutateAsync(input)
      );

      finalizeLogin({
        me: meUser,
        tenantId,
        updateContext,
        onLogin,
        setIsLoggedInOnce,
        navigate,
        returnTo,
      });

      return true;
    } catch (err) {
      logger.error("Failed to auto-create user on first SSO login", {
        operation: "autoCreateUserOnFirstLogin",
        error: err,
        userId,
        tenantId,
      });
      return false;
    }
  };

  useEffect(() => {
    if (isAuthenticated && !isLoggedInOnce && !hasAttemptedAutoCreate.current) {
      hasAttemptedAutoCreate.current = true;
      const fetchTokenAndClaims = async () => {
        // In-app redirects to /auth carry returnTo in router state. After the Auth0 callback the
        // handoff's own state can be replaced by the index route's stateless redirect, so its
        // stored copy is the one that counts (LAR-565). Taken before anything can fail, so a
        // sign-in that ends in a logout can't pass it on to a later one in this tab.
        const fromState = (location.state as { returnTo?: string } | undefined)
          ?.returnTo;
        const fromStorage = takePendingReturnTo();
        const returnTo = fromState ?? fromStorage;

        try {
          const token = requireAccessToken(
            await getAccessTokenSilently(),
            "SignInView.fetchTokenAndClaims"
          );
          useUserTokenStore.getState().setToken(token);

          const decodedToken: Claims = jwtDecode(token);
          const firstLogin = decodedToken[
            `${claimsNamespace}/first_login`
          ] as boolean;

          const tenantId = decodedToken[
            `${claimsNamespace}/organization_id`
          ] as string;

          const permissions = decodedToken[`permissions`] as string[];
          useUserTokenStore.getState().setPermissions(permissions || []);

          let meUser: User | undefined = me.data;
          if (!meUser) {
            const meResponse = await userService.getMe();
            if (meResponse.success) {
              meUser = meResponse.value;
            }
          }

          if (firstLogin) {
            const didAuto = await attemptAutoCreateUser(
              decodedToken,
              tenantId,
              meUser,
              returnTo
            );
            if (didAuto) return;
            onLogin();
            navigate(AppRoutes.onboarding);
          } else {
            finalizeLogin({
              me: meUser,
              tenantId,
              updateContext,
              onLogin,
              setIsLoggedInOnce,
              navigate,
              returnTo,
            });
          }
        } catch (error) {
          logger.error("Failed to fetch token or claims", {
            operation: "completeSignIn",
            error,
          });
          handleLogout(logout);
          setAuthError("Failed to fetch token or claims");
          setLoading(false);
          hasAttemptedAutoCreate.current = false;
        }
      };
      setLoading(true);
      setTimeout(() => {
        fetchTokenAndClaims();
      }, 1000);
    }

    // Clean up URL parameters if user is returning from organization picker
    if (isAuthenticated && isLoggedInOnce) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("organization_switched")) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("organization_switched");
        window.history.replaceState({}, "", newUrl.toString());
        navigate(AppRoutes.dashboard);
      }
    }

    if (error) {
      setAuthError(error.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isAuthenticated, navigate, isLoggedInOnce]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoggedInOnce(false);
      hasAttemptedAutoCreate.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("invitation") && urlParams.has("organization")) {
      handleAuthAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show the loading view while:
  // - Auth0 is bootstrapping or processing the post-login `?code=...` callback
  //   (covers the initial render where isAuthenticated is still false but the
  //   code/state exchange is in flight),
  // - our local fetchTokenAndClaims work is running, or
  // - Auth0 already says we're authenticated but we haven't finished our own
  //   onLogin/finalizeLogin flow yet, as long as no auth error needs to be shown.
  const isAuthenticating =
    isAuth0Loading ||
    loading ||
    (isAuthenticated && !isLoggedInOnce && !authError && !error);

  const handleAuthAction = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const invitation = urlParams.get("invitation") || undefined;
    const organization = urlParams.get("organization") || undefined;
    const returnTo =
      (location.state as { returnTo?: string } | undefined)?.returnTo ||
      AppRoutes.auth;

    if (error || isAuthenticated) {
      handleLogout(logout);
      useUserStore.getState().clear();
    } else {
      loginWithRedirect({
        appState: { returnTo },
        authorizationParams: {
          redirect_uri: `${window.location.origin}${AppRoutes.auth}`,
          ...(invitation !== undefined ? { invitation } : {}),
          ...(organization !== undefined ? { organization } : {}),
        },
      }).catch((loginError) =>
        logger.error("Failed to start the Auth0 login redirect", {
          operation: "signIn",
          error: loginError,
          auth0OrganizationId: organization,
          hasInvitation: invitation !== undefined,
        })
      );
    }
  };

  return (
    <div className="relative h-screen w-screen">
      <div className="absolute top-4 right-4 z-10">
        <AuthThemeToggle id="auth-theme-toggle" />
      </div>
      {isAuthenticating ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoadingView />
        </div>
      ) : (
        <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <div className="mx-auto w-fit">
              <Logo src={t("auth.logo")} />
            </div>
            <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight">
              {t("auth.text")}
            </h2>
          </div>
          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            <div>
              <div className="flex w-full flex-col gap-6">
                {authError && (
                  <div className="text-destructive mb-4 text-sm">{`Oops, something went wrong during sign in: ${authError}`}</div>
                )}
                <Button type="submit" onClick={handleAuthAction}>
                  {error ? t("auth.button.signOut") : t("auth.button.signIn")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignInView;
